/**
 * Merge-jar creature sizing by area. Bodies are circles, so area alone sets each radius:
 * the anchor creature (Dino) fills a fixed share of the usable jar and every tier is a fixed
 * area step bigger than the one before. The dragon continues the same progression; it does
 * not set Dino's size.
 */

/**
 * Radius of each tier relative to the anchor tier (anchor = 1). Null when the anchor creature is
 * not in the chain (a capped chain keeps the legacy radius table).
 * @param {Array<{ assetId: string }>} chain active evolution chain
 * @param {{ anchorAssetId: string, areaGrowth: number } | null | undefined} sizing
 * @returns {number[] | null}
 */
export function getEvolutionRadiusSteps(chain, sizing) {
  if (!sizing || !chain || !chain.length) return null;
  var anchor = -1;
  for (var i = 0; i < chain.length; i++) {
    if (chain[i] && chain[i].assetId === sizing.anchorAssetId) {
      anchor = i;
      break;
    }
  }
  var growth = Number(sizing.areaGrowth);
  if (anchor < 0 || !isFinite(growth) || growth <= 1) return null;
  var step = Math.sqrt(growth);
  return chain.map(function (_, tier) { return Math.pow(step, tier - anchor); });
}

/**
 * @param {Array<{ assetId: string }>} chain active evolution chain
 * @param {number} interiorWidth usable jar width (between the inner wall faces)
 * @param {number} interiorHeight usable jar height (rim to floor)
 * @param {{ anchorAssetId: string, areaFraction: number, areaGrowth: number } | null | undefined} sizing
 * @returns {number[] | null} body radius per tier, or null to keep the legacy table
 */
export function computeEvolutionRadii(chain, interiorWidth, interiorHeight, sizing) {
  var steps = getEvolutionRadiusSteps(chain, sizing);
  var fraction = sizing ? Number(sizing.areaFraction) : NaN;
  if (!steps || !(interiorWidth > 0) || !(interiorHeight > 0) || !(fraction > 0)) return null;
  var anchorRadius = Math.sqrt(interiorWidth * interiorHeight * fraction / Math.PI);
  // Narrow jars (tall portrait, Fall preview) must still hold the biggest creature:
  // shrink the whole chain evenly only when its largest circle cannot fit.
  var maxDiameter = 2 * anchorRadius * steps[steps.length - 1];
  var fit = Math.min(1, Math.min(interiorWidth, interiorHeight) * 0.98 / maxDiameter);
  return steps.map(function (step) { return anchorRadius * step * fit; });
}
